'use client'

import { useState } from 'react'

import type { SubScoreData } from '@/components/client/product/scorecard/scorecard-types'
import { useTranslation } from '@/hooks/client/use-translation'
import type { RatingDescriptor } from '@/lib/product/rating-descriptor'

const SCORES_TO_SHOW_INITIALLY = 3

const getScoreColor = (score: number) => {
  if (score >= 4.5) {
    return { bg: 'bg-green-500', text: 'text-green-600' }
  }
  if (score >= 4.0) {
    return { bg: 'bg-lime-500', text: 'text-lime-600' }
  }
  if (score >= 3.5) {
    return { bg: 'bg-yellow-500', text: 'text-yellow-600' }
  }
  if (score >= 3.0) {
    return { bg: 'bg-orange-400', text: 'text-orange-500' }
  }
  return { bg: 'bg-red-500', text: 'text-red-600' }
}

const useComparisonLabel = () => {
  const { t } = useTranslation()
  return (comparison: RatingDescriptor) => {
    if (comparison === 'excellent') {
      return t('client.product.excellent', 'Excellent')
    }
    if (comparison === 'above') {
      return t('client.product.aboveAvg', 'Above avg')
    }
    if (comparison === 'below') {
      return t('client.product.belowAvg', 'Below avg')
    }
    return t('client.product.average', 'Average')
  }
}

const ReviewRow = ({ subScore }: { subScore: SubScoreData }) => {
  const { t } = useTranslation()
  const getComparisonLabel = useComparisonLabel()
  const [isOpen, setIsOpen] = useState(false)
  const percentage = (subScore.score / 5) * 100
  const color = getScoreColor(subScore.score)

  return (
    <div
      className={`overflow-hidden rounded-xl border bg-white transition-all duration-300 ${isOpen ? 'border-blue-200 shadow-md' : 'border-slate-100 hover:border-blue-200'}`}
    >
      <button
        type="button"
        data-tracking="ignore"
        aria-expanded={isOpen}
        data-ht-disclosure-trigger={`rating:${subScore.key}`}
        className="flex w-full cursor-pointer items-center justify-between px-4 py-3 transition-colors hover:bg-slate-50/50"
        onClick={() => setIsOpen((open) => !open)}
      >
        <span className="text-navy md:text-md min-w-0 truncate text-base font-semibold">{subScore.label}</span>
        <div className="flex shrink-0 items-center gap-3">
          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${color.bg}`} style={{ width: `${percentage}%` }} />
          </div>
          <span className={`w-6 text-right text-base font-bold md:text-sm ${color.text}`}>
            {subScore.score.toFixed(1)}
          </span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className={`h-4 w-4 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="px-4 pt-0 pb-4">
          <div className="border-t border-slate-50 pt-3">
            <div className="mb-2 flex items-baseline justify-between">
              <span className="text-lapis text-base font-bold md:text-sm">
                {getComparisonLabel(subScore.comparison)}
              </span>
              <span className="text-sm text-slate-400 md:text-xs">
                {t('product.score', 'Score:')} {subScore.score.toFixed(1)}/5
              </span>
            </div>
            <p className="text-md !max-w-none leading-relaxed text-slate-500">{subScore.comment}</p>
          </div>
        </div>
      )}
    </div>
  )
}

interface ExpertReviewProps {
  subScores: SubScoreData[]
}

export const ArticleProductExpertReview = ({ subScores }: ExpertReviewProps) => {
  const { t } = useTranslation()
  const [showAll, setShowAll] = useState(false)

  if (subScores.length === 0) {
    return null
  }

  const visible = showAll ? subScores : subScores.slice(0, SCORES_TO_SHOW_INITIALLY)
  const hasMore = subScores.length > SCORES_TO_SHOW_INITIALLY

  return (
    <div className="w-full border-t border-slate-100 bg-slate-50 p-6">
      <div className="mb-6 flex items-center justify-center">
        <h3 className="text-sm font-bold tracking-wider text-slate-500 uppercase">
          {t('client.product.expertReview', 'Expert Review')}
        </h3>
      </div>

      <div className="flex flex-col gap-2">
        {visible.map((subScore) => (
          <ReviewRow key={subScore.key} subScore={subScore} />
        ))}
      </div>

      {hasMore && (
        <button
          type="button"
          data-tracking="ignore"
          aria-expanded={showAll}
          data-ht-disclosure-trigger="read-more"
          onClick={() => setShowAll((all) => !all)}
          className="text-lapis hover:text-lapis/80 mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-colors hover:bg-blue-50 md:text-xs"
        >
          <span>
            {showAll ? t('client.product.showLess', 'Show Less') : t('client.product.showAll', 'Show All Ratings')}
          </span>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className={`h-3.5 w-3.5 shrink-0 transition-transform ${showAll ? 'rotate-180' : ''}`}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
          </svg>
        </button>
      )}
    </div>
  )
}
